import {useEffect, useMemo, useState} from "react";
import {useEffectPromise} from "./EffectHooks";
import {useDelay} from "./DelayHooks";
import {service as ContextNodeService} from "../Services/ContextNodeService";
import {service as ConfirmationStatusService} from "../Services/ConfirmationStatusService";
import {service as EnvironmentalBenefitService} from "../Services/EnvironmentalBenefitService";
import {useGetQueryStringParameter} from "./RouteHooks";
import {usePrimeCache} from "./ServiceHooks";
import {service as ConservationActionPhaseService} from "../Services/ConservationActionPhaseService";
import {usePermission} from "./PermissionHooks";

/**
 * Provided a benefit summary (or any array of data with an `environmentalBenefitId` property)
 * this function gives an array of valid EnvironmentalBenefit (only ones matching the ones
 * within the `dataset`). The result of this hook is useful for populating a dropdown of available
 * EnvironmentalBenefits for display on a chart or graph, given the dataset that is being used.
 * 
 * An optional array of confirmationStatusIds can also be provided if you want to further filter
 * the benefits to only data in specific confirmationStatusId(s). Just make sure the values in
 * `dataset` also contains `confirmationStatusId` property.
 * 
 * @param dataset
 * @param confirmationStatusIds
 * @returns {[*]}
 */
export const useFilteredBenefits = (dataset, confirmationStatusIds = null) => {
	
	usePrimeCache([ConfirmationStatusService, EnvironmentalBenefitService]);
	const environmentalBenefits = EnvironmentalBenefitService.CachedValues()?.result;
	
	// only show benefits for which we have modeled uplift
	return useMemo(() =>
			environmentalBenefits?.filter((environmentalBenefit) =>
				dataset?.find((contextNode) => {
					
					if( confirmationStatusIds && 
						!confirmationStatusIds?.some( (confirmationStatusId) =>
							String(confirmationStatusId) === String(contextNode.confirmationStatusId)
						)
					) {
						return false;
					}
					
					return String(contextNode.environmentalBenefitId) === String(environmentalBenefit.environmentalBenefitId);
				})),
		[environmentalBenefits, dataset, confirmationStatusIds]
	);
}

export const useBenefitValues = (contextNodeId, contextNodeTypeId = undefined, includeFunders = false, delay = 500) => {
	
	const [delayHasElapsed, resetDelay] = useDelay(delay);
	const [benefitSummary, setBenefitSummary] = useState(null);
	
	const forceReload = () => {
		setBenefitSummary(null);
		resetDelay();
	}
	
	// clear the benefit summary if editMode = true
	// so that when the user returns to the view mode
	// we will reload it. 
	// TODO: we should probably see if
	// we can only perform a reload when the user
	// saves their data for the contextNodeId instead.
	
	const loadBenefitSummary = async (contextNodeId) => {
		try {
			return (await ContextNodeService.GetBenefitsSummary(contextNodeId, contextNodeTypeId, null, includeFunders))?.result ?? null;
		} catch (e) {
			console.log('loadBenefitSummary failed for contextNodeId:', contextNodeId, e);
		}
		return null;
	}
	
	// we use delayHasElapsed to postpone the loading
	// of this API request until all the others are done
	// since this one takes a long time and blocks.
	useEffectPromise(
		async () => {
			if(contextNodeId == null) {
				return null;
			}
			setBenefitSummary(null);
			return loadBenefitSummary(contextNodeId);
		},
		setBenefitSummary,
		[contextNodeId, delayHasElapsed],
		!contextNodeId || delayHasElapsed
	);
	
	return [benefitSummary, forceReload];
}

export const useFilteredBenefitValues = (benefitValues, confirmationStatusId) => {
	return useMemo(() => {
		return benefitValues?.filter((benefitValue, key) => {
			return Number(benefitValue?.confirmationStatusId) === Number(confirmationStatusId)
		}) ?? []
	}, [confirmationStatusId, benefitValues]);
};

export const useBenefitValuesGroupedByEnvironmentalBenefitId = (benefitSummary, totalContextNodeId = null, textfilter = '') => {
	
	const searchTerms = textfilter.toLocaleLowerCase().split(' ').filter(Boolean);
	const filteredEnvironmentBenefits = useFilteredBenefits(benefitSummary);
	
	const benefitSummaryGroup = useMemo(() => {
			return benefitSummary?.reduce((acc, contextNode) => {
				
				const contextNodeId = Number(contextNode.contextNodeId);
				const title = (Number(totalContextNodeId) === Number(contextNodeId)) ? 'Total' : contextNode?.name;
				
				// TODO: consider including name and nicknames in this?
				const titleSearchable = title.toLocaleLowerCase();
				const invisibleSearchables = [
					contextNode?.shortName,
					(Number(totalContextNodeId) === Number(contextNodeId)) ? contextNode?.name : '',
				];
				const invisibleSearchable = (invisibleSearchables).join("\n")?.toLocaleLowerCase()
				const fundersSearchable = (contextNode.Funders ?? []).map((funder) => funder.name).join("\n").toLocaleLowerCase();
				
				let environmentalBenefitId = Number(contextNode.environmentalBenefitId);
				const environmentalBenefit = filteredEnvironmentBenefits?.find((environmentalBenefit) =>
					Number(environmentalBenefit.environmentalBenefitId) === environmentalBenefitId
				)
				
				// filter out things that don't match all of the search terms
				if (searchTerms.length && !searchTerms.every(searchTerm =>
					titleSearchable.includes(searchTerm) ||
					invisibleSearchable.includes(searchTerm) ||
					fundersSearchable.includes(searchTerm)
				)) {
					return acc;
				}
				
				const matchingInvisibles = searchTerms.map(
					(searchTerm) => {
						return invisibleSearchables.find(
							(invisible) => {
								return invisible?.toLocaleLowerCase().includes(searchTerm) ? invisible : false;
							});
					})
					.filter(Boolean)
					.map(invisibleString => " (" + invisibleString + ")")
					.join('');
				
				// ignore filtered environmentalBenefits
				if (environmentalBenefit) {
					acc[contextNodeId] ??= {
						contextNodeId,
						title: title + matchingInvisibles,
						contextNodeTypeId: contextNode?.contextNodeTypeId,
						name: contextNode?.name + matchingInvisibles,
						shortName: contextNode?.shortName + matchingInvisibles,
						link: contextNode?.link,
						EnvironmentalBenefits: [],
						Funders: contextNode?.Funders,
						sbTableSection: (Number(totalContextNodeId) === Number(contextNodeId)) ? 'header' : 'body',
					};
					acc[contextNodeId].EnvironmentalBenefits[environmentalBenefitId] = contextNode;
				}
				
				return acc;
			}, {}) ?? null;
		},
		[benefitSummary, filteredEnvironmentBenefits]
	);
	
	return benefitSummaryGroup ? Object.values(benefitSummaryGroup) : null;
}

export const useAugmentedConservationActionPhases = () => {
	
	usePrimeCache([ConservationActionPhaseService])
	const hasPermission = usePermission();
	
	const conservationActionPhases = ConservationActionPhaseService.CachedValues();
	
	let augmentedConservationActionPhases = [];
	
	if(hasPermission('view_unconfirmed_modeled_uplift')) {
		augmentedConservationActionPhases = conservationActionPhases?.result
			?.filter((phase) => ['eligibility', 'design', 'as-built'].includes(phase.code)) ?? [];
	}
	
	augmentedConservationActionPhases.push({
		code: "confirmed",
		conservationActionPhaseId: 3,
		confirmationStatusId: 3,
		name: "Confirmed"
	});
	
	return augmentedConservationActionPhases;
}
